import { Alert, Spin } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ConnectivityHeatmapDay,
  ConnectivityHeatmapSlot,
} from "src/services/connectivityHeatmapService";
import { cn } from "src/util/cn";

interface ConnectivityHeatmapProps {
  data: ConnectivityHeatmapDay[];
  dateRange: [Dayjs, Dayjs];
  isLoading: boolean;
  isError: boolean;
}

interface HeatmapRow {
  date: string;
  slotsByTime: Map<string, ConnectivityHeatmapSlot>;
}

type LegendStatus = "connected" | "delayed" | "noData" | "powerOn" | "powerOff";

const CONNECTED_COLOR = "#20B86A";
const DELAYED_COLOR = "#5B9074";
const POWER_OFF_COLOR = "#D7385E";
const POWER_ON_COLOR = "#050B18";
const NO_DATA_BORDER_COLOR = "#AEB6CC";
const GRID_BORDER_COLOR = "#D6DEEF";

const VISIBLE_END_HOUR = 24;
const SLOT_MINUTES = 15;
const VISIBLE_SLOTS = (VISIBLE_END_HOUR * 60) / SLOT_MINUTES;

const SLOTS_PER_DAY = VISIBLE_SLOTS;

const LABEL_COLUMN_WIDTH = 110;
const SLOT_GAP = 2;
const ROW_HEIGHT = 36;
const SLOT_HEIGHT = 30;
const POWER_EVENT_MINUTES = 2;
const POWER_EVENT_WIDTH_PERCENT = (POWER_EVENT_MINUTES / SLOT_MINUTES) * 100;

const getTimeLabelLeft = (hour: number) => `${(hour / VISIBLE_END_HOUR) * 100}%`;

const buildRows = (data: ConnectivityHeatmapDay[], [from, to]: [Dayjs, Dayjs]) => {
  const responseByDate = new Map(data.map((item) => [item.date, item]));
  const numberOfDays = to.startOf("day").diff(from.startOf("day"), "day") + 1;

  return Array.from({ length: numberOfDays }, (_, index): HeatmapRow => {
    const date = to.subtract(index, "day");
    const dateString = date.format("YYYY-MM-DD");
    const responseDay = responseByDate.get(dateString);

    return {
      date: dateString,
      slotsByTime: new Map((responseDay?.slots ?? []).map((slot) => [slot.time, slot])),
    };
  });
};

const getSlotTime = (index: number) =>
  dayjs()
    .startOf("day")
    .add(index * SLOT_MINUTES, "minute")
    .format("HH:mm");

const getDayAbbreviation = (date: string, language: string) => {
  const day = dayjs(date).locale("en").format("dddd").toUpperCase();
  const italianMap: Record<string, string> = {
    MONDAY: "Lun",
    TUESDAY: "Mar",
    WEDNESDAY: "Mer",
    THURSDAY: "Gio",
    FRIDAY: "Ven",
    SATURDAY: "Sab",
    SUNDAY: "Dom",
  };

  const englishMap: Record<string, string> = {
    MONDAY: "Mon",
    TUESDAY: "Tue",
    WEDNESDAY: "Wed",
    THURSDAY: "Thu",
    FRIDAY: "Fri",
    SATURDAY: "Sat",
    SUNDAY: "Sun",
  };

  const map = language.toLowerCase().startsWith("it") ? italianMap : englishMap;

  return map[day] ?? day.slice(0, 3);
};

const getSlotBaseStatus = (slot?: ConnectivityHeatmapSlot): LegendStatus => {
  if (!slot) return "noData";
  return slot.delay ? "delayed" : "connected";
};

const getSlotPowerEvents = (slot?: ConnectivityHeatmapSlot) =>
  slot?.power?.filter((value): value is boolean => typeof value === "boolean") ?? [];

const ConnectivityHeatmap = ({ data, dateRange, isLoading, isError }: ConnectivityHeatmapProps) => {
  const { t, i18n } = useTranslation();

  const [hiddenStatuses] = useState<Set<LegendStatus>>(() => new Set());

  const rows = useMemo(() => buildRows(data, dateRange), [data, dateRange]);

  const timeLabels = Array.from(
    { length: VISIBLE_END_HOUR + 1 },
    (_, index) => `${String(index).padStart(2, "0")}:00`
  );

  const legend = [
    { label: t("connected"), color: CONNECTED_COLOR, status: "connected" as const },
    { label: t("delayed"), color: DELAYED_COLOR, status: "delayed" as const },
    { label: t("no_data"), color: "transparent", status: "noData" as const },
    { label: t("power_on"), color: POWER_ON_COLOR, status: "powerOn" as const },
    { label: t("power_off"), color: POWER_OFF_COLOR, status: "powerOff" as const },
  ];

  if (isLoading) {
    return (
      <div className="mt-5 flex h-[280px] items-center justify-center rounded-[8px] border border-primary6 bg-gray1">
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("connectivity_graph_load_failed")}
        className="!mt-5"
      />
    );
  }

  return (
    <div className="mt-5 overflow-hidden rounded-[8px] border border-primary6 bg-gray1 px-3 pb-4 pt-4">
      <div className="w-full pr-[8px]">
        {rows.map((row, rowIndex) => (
          <div
            key={row.date}
            className="grid"
            style={{
              gridTemplateColumns: `${LABEL_COLUMN_WIDTH}px 1fr`,
            }}
          >
            <div
              className="flex items-center gap-[6px] pr-1.5 text-xs font-[500] text-gray8 whitespace-nowrap"
              style={{ height: ROW_HEIGHT }}
            >
              {getDayAbbreviation(row.date, i18n.language)}
              <span>
                {dayjs(row.date).format(
                  i18n.language.toLowerCase().startsWith("it") ? "DD/MM/YYYY" : "YYYY-MM-DD"
                )}
              </span>
            </div>

            <div
              className={cn(
                "border-l border-t bg-gray1 px-[3px]",
                rowIndex === rows.length - 1 && "border-b"
              )}
              style={{
                height: ROW_HEIGHT,
                borderColor: GRID_BORDER_COLOR,
              }}
            >
              <div
                className="grid items-center h-full"
                style={{
                  gridTemplateColumns: `repeat(${SLOTS_PER_DAY}, minmax(0, 1fr))`,
                  columnGap: SLOT_GAP,
                }}
              >
                {Array.from({ length: SLOTS_PER_DAY }, (_, index) => {
                  const slot = row.slotsByTime.get(getSlotTime(index));
                  const baseStatus = getSlotBaseStatus(slot);
                  const powerEvents = getSlotPowerEvents(slot);

                  const isBaseVisible = !hiddenStatuses.has(baseStatus);

                  const showNoData = !hiddenStatuses.has("noData") && !slot;

                  const baseColor = baseStatus === "delayed" ? DELAYED_COLOR : CONNECTED_COLOR;

                  if (!slot && !showNoData) return <span key={index} />;

                  return (
                    <span
                      key={index}
                      className="relative overflow-hidden"
                      style={{
                        width: "100%",
                        height: SLOT_HEIGHT,
                        backgroundColor: slot && isBaseVisible ? baseColor : "transparent",
                      }}
                    >
                      {powerEvents
                        .map((state, eventIndex) => ({ eventIndex, state }))
                        .filter(({ state }) =>
                          state
                            ? !hiddenStatuses.has("powerOn")
                            : !hiddenStatuses.has("powerOff")
                        )
                        .map(({ eventIndex, state }) => (
                          <span
                            key={`${eventIndex}-${String(state)}`}
                            className={cn(
                              "absolute bottom-0 h-[6px]",
                              state ? "z-10" : "z-20"
                            )}
                            style={{
                              left: `${(eventIndex / powerEvents.length) * 100}%`,
                              width: `${POWER_EVENT_WIDTH_PERCENT}%`,
                              backgroundColor: state ? POWER_ON_COLOR : POWER_OFF_COLOR,
                            }}
                          />
                        ))}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        <div
          className="grid"
          style={{
            gridTemplateColumns: `${LABEL_COLUMN_WIDTH}px 1fr`,
          }}
        >
          <div />
          <div className="relative h-[24px] pt-1.5 text-[11px] font-[500] text-gray8">
            <div
              className="relative h-full"
              style={{
                // marginInline: SHOULD_KEEP_TIME_LABELS_INSIDE_GRAPH ? TIME_LABEL_EDGE_INSET : 0,
                // marginRight: "18px",
              }}
            >
              {timeLabels.map((label, index) => {
                const left = getTimeLabelLeft(index);

                return (
                  <span
                    key={`${label}-${index}`}
                    className="absolute -translate-x-1/2 whitespace-nowrap"
                    style={{ left }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-gray9">
          {legend.map((item) => (
            <button
              key={item.label}
              type="button"
              className="flex items-center gap-2 cursor-default"
              // onClick={() =>
              //   setHiddenStatuses((prev) => {
              //     const next = new Set(prev);

              //     if (next.has(item.status)) next.delete(item.status);
              //     else next.add(item.status);

              //     return next;
              //   })
              // }
              disabled
            >
              <span
                className={cn("h-3 w-3 border", hiddenStatuses.has(item.status) && "opacity-45")}
                style={{
                  backgroundColor: item.color,
                  borderColor: item.status === "noData" ? NO_DATA_BORDER_COLOR : item.color,
                }}
              />

              <span
                className={cn(
                  "font-[500] text-gray8",
                  hiddenStatuses.has(item.status) && "opacity-60"
                )}
              >
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConnectivityHeatmap;
